import {
    NinchatAttributes,
    NinchatSettings,
} from 'api/types'
import {
    slaDurationNumericRegExp,
    slaDurationRegExp,
} from 'utils/regexp'

type QueueWithSettings = {
    queue_settings?: NinchatSettings.NinchatQueueSettings
}

type QueueWithTicketAttributes = QueueWithSettings & {
    queue_attrs?: Pick<NinchatAttributes.NinchatQueueAttributes, 'unassigned_registered'>
}

export const defaultQueueSlaReminderTime = '5m'
export const defaultQueueSlaWarningTime = '10m'
export const defaultTicketSlaReminderTime = '5m'
export const defaultTicketSlaWarningTime = '10m'

type GroupManagedQueueSettings = Pick<
    NinchatSettings.NinchatQueueSettings,
    'assign_oldest_ticket' | 'queue_sla' | 'queue_sla_reminder_time' | 'queue_sla_warning_time' |
    'sidebar_queue_counter' | 'ticket_sla' | 'ticket_sla_reminder_time' | 'ticket_sla_warning_time'
>

export const getGroupManagedQueueSettings = (
    queueSettings?: NinchatSettings.NinchatQueueSettings,
): GroupManagedQueueSettings => ({
    assign_oldest_ticket: queueSettings?.assign_oldest_ticket ?? false,
    queue_sla: queueSettings?.queue_sla ?? false,
    queue_sla_reminder_time: queueSettings?.queue_sla_reminder_time ?? defaultQueueSlaReminderTime,
    queue_sla_warning_time: queueSettings?.queue_sla_warning_time ?? defaultQueueSlaWarningTime,
    sidebar_queue_counter: queueSettings?.sidebar_queue_counter ?? 'queueing_time',
    ticket_sla: queueSettings?.ticket_sla ?? false,
    ticket_sla_reminder_time: queueSettings?.ticket_sla_reminder_time ?? defaultTicketSlaReminderTime,
    ticket_sla_warning_time: queueSettings?.ticket_sla_warning_time ?? defaultTicketSlaWarningTime,
})

export const getActiveQueueSettings = ({
    parentGroupQueue,
    queue,
}: {
    parentGroupQueue?: QueueWithSettings
    queue?: QueueWithSettings
}): NinchatSettings.NinchatQueueSettings | undefined => {
    if (!parentGroupQueue) {
        return queue?.queue_settings
    }

    return {
        ...queue?.queue_settings,
        ...getGroupManagedQueueSettings(parentGroupQueue.queue_settings),
    }
}

const getQueueRegisteredTicketCount = (
    queue?: QueueWithTicketAttributes,
): number => queue?.queue_attrs?.unassigned_registered ?? 0

export const getActiveQueueRegisteredTicketCount = ({
    parentGroupQueue,
    queue,
}: {
    parentGroupQueue?: QueueWithSettings
    queue?: QueueWithTicketAttributes
}): number => (
    getActiveQueueSettings({
        parentGroupQueue,
        queue,
    })?.audience_ticketing
        ? getQueueRegisteredTicketCount(queue)
        : 0
)

export const getActiveQueueAssignableTicketCount = ({
    parentGroupQueue,
    queue,
}: {
    parentGroupQueue?: QueueWithSettings
    queue?: QueueWithTicketAttributes
}): number => {
    const queueSettings = getActiveQueueSettings({
        parentGroupQueue,
        queue,
    })

    return queueSettings?.audience_ticketing && queueSettings.assign_oldest_ticket
        ? getQueueRegisteredTicketCount(queue)
        : 0
}

export type SlaDurationUnit = 'm' | 'h' | 'd'
export type SlaDuration = {amount: string, unit: SlaDurationUnit}

const SLA_DURATION_SECONDS = {
    d: 24 * 60 * 60,
    h: 60 * 60,
    m: 60,
} as const

const normalizeSlaDuration = (value: string | undefined): string | undefined => {
    if (slaDurationRegExp.test(value || '')) {
        return value
    }

    if (slaDurationNumericRegExp.test(value || '')) {
        return `${value}m`
    }

    return undefined
}

export const parseSlaDuration = (value: string | undefined, fallback: string): SlaDuration => {
    const duration = normalizeSlaDuration(value) ?? normalizeSlaDuration(fallback) ?? fallback

    return {
        amount: duration.slice(0, -1),
        unit: duration.slice(-1) as SlaDurationUnit,
    }
}

export const parseSlaDurationSeconds = (
    value: string | undefined,
    fallback?: string,
): number | undefined => {
    const duration = normalizeSlaDuration(value) ?? normalizeSlaDuration(fallback)

    if (!duration) {
        return undefined
    }

    const unit = duration.slice(-1) as keyof typeof SLA_DURATION_SECONDS
    const amount = Number(duration.slice(0, -1))

    return amount * SLA_DURATION_SECONDS[unit]
}
